// src/utils/itemLedgerBalance.js
// P4-5c (31 Oct 2026) — the running balance on the Item Ledger and Inventory
// Ledger pages.
//
// WHY THIS FILE EXISTS
// inventory_transactions holds store AND site movements, and both carry a
// stock_effect. The ledger pages added every row, so ITEM-00458 showed 5 while
// Stock List showed 0: its SITE_GRN_IN +5 never touched inventory_summary.
// The balance on these pages means STORE stock only, the same figure as
// inventory_summary.qty.
//
// What counts is decided from the WRITERS in inventoryService.js, never from
// the name or from site_id (an MR_ISSUE carries a site_id and IS a store move):
//   SITE_GRN_IN, SITE_TRANSFER_OUT / IN / REVERSED write site_inventory_summary
//   only. STOCK_TRANSFER_IN is logged +qty, but transferStock() credits nothing,
//   so counting it would hide the qty the OUT row took off the store.
// A row without movement_type is junk from the deleted legacy triggers.
// Site rows stay on the page as history; they just never move the balance.
//
// A new movement type must be added here or to the store list in
// itemLedgerBalance.test.js — the guard test there fails until then.
// Pure function → covered by src/utils/itemLedgerBalance.test.js (Jest).

import { roundQty } from "./stockPlan";

export const NOT_STORE_MOVEMENTS = [
  "SITE_GRN_IN",
  "SITE_TRANSFER_OUT",
  "SITE_TRANSFER_IN",
  "SITE_TRANSFER_REVERSED",
  "STOCK_TRANSFER_IN",
];

const clean = (value) => String(value ?? "").trim();

const effectOf = (row) => {
  const effect = Number(row?.stock_effect);
  return Number.isFinite(effect) ? effect : 0;
};

/** @returns {"store"|"site"|"junk"} what a ledger row is, for the balance */
export const ledgerRowKind = (row) => {
  const type = clean(row?.movement_type);
  if (!type) return "junk";
  return NOT_STORE_MOVEMENTS.includes(type) ? "site" : "store";
};

/**
 * Adds ledger_kind + store_balance (the store stock after that row) to each
 * row. Balances run per item, oldest first, and the rows come back in the same
 * order they came in. The input is not changed.
 *
 * @param {Array} rowsNewestFirst ledger rows, newest first, as the pages show them
 * @returns {Array} new row objects, same order
 */
export const withStoreBalance = (rowsNewestFirst = []) => {
  const balances = new Map();

  const oldestFirst = [...rowsNewestFirst].reverse().map((row) => {
    const key = clean(row?.item_id) || clean(row?.item_code) || clean(row?.item_name);
    const kind = ledgerRowKind(row);
    const before = balances.get(key) || 0;
    const after = kind === "store" ? roundQty(before + effectOf(row)) : before;
    balances.set(key, after);
    return { ...row, ledger_kind: kind, store_balance: after };
  });

  return oldestFirst.reverse();
};

/** @returns {string} what the Effect column shows: "+5", "-5", "0", "site", "not counted" */
export const ledgerEffectText = (row) => {
  const kind = ledgerRowKind(row);
  if (kind === "site") return "site";
  if (kind === "junk") return "not counted";
  const effect = effectOf(row);
  return effect > 0 ? `+${effect}` : String(effect);
};
